import "./shell/courseTabs.css";
import { GENERAL_DASHBOARD_PATH } from "@/shared/lib/defaultCourse";
// src/shared/dashboard/AppShell.tsx
//
// The ONE global navigation shell for every authenticated in-app product:
// paper canvas, a single desktop navigation rail, a compact phone header and an
// optional support rail on genuinely wide viewports. The shell owns ALL nav
// chrome; pages render as pure content.
//
// Nav comes from the active vertical's `dashboard.sidebar` (see shell/nav.ts —
// flat items are pills; an item's `children` are its contextual rail). This file
// is composition only; the pieces live in ./shell/*.
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useAuth } from "@/shared/contexts/AuthContext";
import { LocaleOverride, useLanguage } from "@/shared/i18n/LanguageContext";
import { useSubscription } from "@/shared/contexts/SubscriptionContext";
import {
  useVertical,
} from "@/shared/contexts/VerticalContext";
import { SubscriptionBanner } from "@/shared/components/SubscriptionBanner";
import {
  isInstructor,
  STUDIO_QUICK_ACCESS_EMAIL,
} from "@/shared/lib/instructorAccess";
import { LayoutGrid, Sparkles, LogOut, X } from "lucide-react";
import { useProductDemo } from "@/shared/contexts/ProductDemoContext";
import {
  matchesPath,
  resolveActiveSection,
  resolveShellChrome,
  showLeftMenu,
  showRightSidebar,
  toSections,
} from "@/shared/dashboard/shell/nav";
import { navLabel } from "@/shared/dashboard/shell/navLabels";
import { verticalShortLabel } from "@/shared/lib/verticalLabels";
import { Icon } from "@/shared/dashboard/shell/Icon";
import { TopBar } from "@/shared/dashboard/shell/TopBar";
import { Sidebar } from "@/shared/dashboard/shell/Sidebar";
import { MobileNav } from "@/shared/dashboard/shell/MobileNav";
import { MentorPanel } from "@/shared/dashboard/shell/MentorPanel";
import { MessageInstructorWidget } from "@/shared/dashboard/shell/MessageInstructorWidget";
import { FAU_INSTRUCTOR_CONTACT } from "@/shared/lib/instructorContact";
import { go } from "@/shared/dashboard/shell/navigate";
import { CRISIS_PROGRAM_COURSE_ID } from "@/shared/lib/cohortCourse";
import { courseLocale } from "@/shared/lib/courseLocale";
import { ProductSwitcher } from "@/shared/dashboard/shell/ProductSwitcher";
import { LanguageSelect } from "@/shared/dashboard/shell/LanguageSelect";
import {
  clearDesktopApp,
  closeDesktopApp,
  readDesktopApp,
} from "@/shared/dashboard/desktopMode";
import { WorkspaceShell } from "@/shared/dashboard/workspace/WorkspaceShell";

const SKIP_TO_CONTENT: Record<string, string> = {
  en: "Skip to main content",
  uk: "Перейти до основного вмісту",
  pl: "Przejdź do głównej treści",
  ro: "Sari la conținutul principal",
};

const DESKTOP_COPY: Record<string, { desktop: string; close: string }> = {
  en: { desktop: "Desktop", close: "Close app" },
  uk: { desktop: "Робочий стіл", close: "Закрити застосунок" },
  pl: { desktop: "Pulpit", close: "Zamknij aplikację" },
  ro: { desktop: "Desktop", close: "Închide aplicația" },
};

const TRAIN_NAV_META: Record<string, { id: string; label: string }> = {
  courses: { id: "train-library", label: "Library" },
  programs: { id: "train-library", label: "Library" },
  supervisor: { id: "train-debrief", label: "Debrief" },
  coach: { id: "train-debrief", label: "Debrief" },
  profile: { id: "train-account", label: "Account" },
};

const COURSE_WORKSPACE_COPY: Record<
  string,
  {
    home: string;
    library: string;
    generalDashboard: string;
    backToCourse: string;
    coach: string;
    account: string;
    overview: string;
    semester: string;
    modules: string;
    resources: string;
    fallTerm: string;
    selfPaced: string;
    assignedCourse: string;
  }
> = {
  en: {
    home: "Course home",
    library: "Program library",
    generalDashboard: "General dashboard",
    backToCourse: "Back to course",
    coach: "AI Coach",
    account: "Account",
    overview: "Overview",
    semester: "Semester",
    modules: "Modules",
    resources: "Resources",
    fallTerm: "Fall 2026",
    selfPaced: "Self-paced",
    assignedCourse: "Assigned course",
  },
  uk: {
    home: "Головна курсу",
    library: "Бібліотека програм",
    generalDashboard: "Загальна панель",
    backToCourse: "Назад до курсу",
    coach: "AI-тренер",
    account: "Профіль",
    overview: "Огляд",
    semester: "Семестр",
    modules: "Модулі",
    resources: "Ресурси",
    fallTerm: "Осінь 2026",
    selfPaced: "У власному темпі",
    assignedCourse: "Призначений курс",
  },
  pl: {
    home: "Strona kursu",
    library: "Biblioteka programów",
    generalDashboard: "Panel główny",
    backToCourse: "Powrót do kursu",
    coach: "Trener AI",
    account: "Konto",
    overview: "Przegląd",
    semester: "Semestr",
    modules: "Moduły",
    resources: "Materiały",
    fallTerm: "Jesień 2026",
    selfPaced: "Własne tempo",
    assignedCourse: "Przypisany kurs",
  },
  ro: {
    home: "Pagina cursului",
    library: "Biblioteca de programe",
    generalDashboard: "Panou general",
    backToCourse: "Înapoi la curs",
    coach: "Antrenor AI",
    account: "Cont",
    overview: "Prezentare",
    semester: "Semestru",
    modules: "Module",
    resources: "Resurse",
    fallTerm: "Toamna 2026",
    selfPaced: "Ritm propriu",
    assignedCourse: "Curs atribuit",
  },
};

export function AppShell({
  pathname,
  children,
  onSignOut,
}: {
  pathname: string;
  children: ReactNode;
  onSignOut?: () => void;
}) {
  const { signOut, user, loading: authLoading } = useAuth();
  const { locale } = useLanguage();
  const { hasAccess, loading: subscriptionLoading } = useSubscription();
  const {
    verticalConfig,
    defaultCourseId,
    branding,
    loading: verticalLoading,
  } = useVertical();
  const { dashboard } = verticalConfig;
  // The course workspace chrome speaks the course's language, not the account's:
  // an English-only cohort must not get a translated rail over English content.
  const courseCopy =
    COURSE_WORKSPACE_COPY[courseLocale(defaultCourseId, locale)] ??
    COURSE_WORKSPACE_COPY.en;
  // Guest product demo (see DemoVerticalProvider): the shell owns the "Exit
  // demo" affordance so the reused product pages stay pure content.
  const guestDemo = useProductDemo()?.guest === true;
  const sidebar = dashboard.sidebar;

  const generalConsumer = verticalConfig.id === "general" && !defaultCourseId;
  const consumerDesktopHome =
    generalConsumer && matchesPath("/dashboard", pathname);
  const desktopApp = generalConsumer ? readDesktopApp() : null;

  // A member whose workspace assigned a course gets it as its own nav entry,
  // pinned first: the course IS their main thing, and everything else is what
  // they browse around it.
  //
  // It also REPLACES Dashboard for them. The neutral entry redirects straight
  // into the assigned course (see DashboardEntry), so keeping both meant two
  // menu items that land in the same place — you clicked Dashboard and arrived
  // at My course.
  const assignedCoursePath = defaultCourseId
    ? `/course/${encodeURIComponent(defaultCourseId)}`
    : "";
  const assignedCourseRoute =
    !!assignedCoursePath && pathname.startsWith(assignedCoursePath);
  const assignedCourseWeekRoute =
    assignedCourseRoute && /\/week\/\d+\/?$/.test(pathname.split("?")[0]);
  // The language this shell renders in. Inside an English-only course it is the
  // course's; everywhere else it is the account's. One value, so the skip link,
  // the chrome copy and everything under LocaleOverride cannot disagree.
  const shellLocale = assignedCourseRoute
    ? courseLocale(defaultCourseId, locale)
    : locale;
  const isFauCourse = defaultCourseId === CRISIS_PROGRAM_COURSE_ID;
  const courseWorkspaceRoute =
    matchesPath("/course", pathname) ||
    // FAU's program library belongs to the general workspace; only an
    // individual course should carry course navigation on that path.
    (!isFauCourse && matchesPath("/courses", pathname)) ||
    matchesPath("/supervisor", pathname) ||
    matchesPath("/profile", pathname);
  const courseWorkspace =
    !!defaultCourseId &&
    verticalConfig.id === "general" &&
    courseWorkspaceRoute;
  const courseOrganization =
    branding.companyName ||
    (isFauCourse ? "FAU" : "Workspace");
  const courseTitle = isFauCourse
    ? "Crisis Negotiations"
    : courseCopy.assignedCourse;
  const courseTerm = isFauCourse ? courseCopy.fallTerm : courseCopy.selfPaced;
  const coursePathAnchor = isFauCourse ? "semester-path" : "course-path";
  const coursePathLabel = isFauCourse
    ? courseCopy.semester
    : courseCopy.modules;
  const primarySections = useMemo(() => {
    const resolved = toSections(sidebar, dashboard.features).map((section) => {
      const trainMeta =
        verticalConfig.id === "general" && !!defaultCourseId
          ? TRAIN_NAV_META[section.id]
          : undefined;
      return trainMeta ? { ...section, ...trainMeta } : section;
    });
    if (!defaultCourseId || !dashboard.features.courses) return resolved;
    const coursePath = `/course/${encodeURIComponent(defaultCourseId)}`;
    if (resolved.some((section) => section.path === coursePath))
      return resolved;
    const assignedSections = [
      {
        id:
          verticalConfig.id === "general" ? "train-course" : "assigned-course",
        path: coursePath,
        label: verticalConfig.id === "general" ? "Course" : "My course",
        icon: "graduation-cap",
        rail: [],
      },
      ...resolved.filter((section) => section.path !== "/dashboard"),
    ];
    if (!courseWorkspace) return assignedSections;
    return [
      {
        id: "course-home",
        path: coursePath,
        label: courseCopy.home,
        icon: "graduation-cap",
        rail: [],
      },
      isFauCourse
        ? {
            id: "course-dashboard",
            path: GENERAL_DASHBOARD_PATH,
            label: courseCopy.generalDashboard,
            icon: "layout-dashboard",
            rail: [],
          }
        : {
            id: "course-library",
            path: "/courses",
            label: courseCopy.library,
            icon: "book-open",
            aliasPaths: ["/course"],
            rail: [],
          },
      ...(!isFauCourse
        ? [
            {
              id: "course-coach",
              path: "/supervisor",
              label: courseCopy.coach,
              icon: "life-buoy",
              rail: [],
            },
          ]
        : []),
      {
        id: "course-account",
        path: "/profile",
        label: courseCopy.account,
        icon: "user",
        rail: [],
      },
    ];
  }, [
    sidebar,
    dashboard.features,
    defaultCourseId,
    verticalConfig.id,
    courseWorkspace,
    courseCopy,
    isFauCourse,
  ]);

  const navigationPath = pathname.includes("?")
    ? pathname
    : `${pathname}${window.location.search}`;
  const active = useMemo(
    () => resolveActiveSection(primarySections, navigationPath),
    [primarySections, navigationPath],
  );
  const courseContextSections = useMemo(() => {
    const sections = [
      {
        id: "course-overview",
        path: `${assignedCoursePath}#course-overview`,
        label: courseCopy.overview,
        icon: "layout-dashboard",
        rail: [],
      },
      {
        id: "course-path",
        path: `${assignedCoursePath}#${coursePathAnchor}`,
        label: coursePathLabel,
        icon: isFauCourse ? "calendar-days" : "list-checks",
        rail: [],
      },
    ];
    if (isFauCourse) {
      sections.push({
        id: "course-resources",
        path: `${assignedCoursePath}#course-resources`,
        label: courseCopy.resources,
        icon: "book-open",
        rail: [],
      });
    }
    return sections;
  }, [
    assignedCoursePath,
    courseCopy.overview,
    courseCopy.resources,
    coursePathAnchor,
    coursePathLabel,
    isFauCourse,
  ]);
  const [activeCourseSection, setActiveCourseSection] = useState(() => {
    if (assignedCourseWeekRoute) return "course-path";
    if (window.location.hash === `#${coursePathAnchor}`) return "course-path";
    if (window.location.hash === "#course-resources") return "course-resources";
    return "course-overview";
  });

  useEffect(() => {
    if (!assignedCourseRoute) return;
    if (assignedCourseWeekRoute) setActiveCourseSection("course-path");
    else if (window.location.hash === `#${coursePathAnchor}`)
      setActiveCourseSection("course-path");
    else if (window.location.hash === "#course-resources")
      setActiveCourseSection("course-resources");
    else setActiveCourseSection("course-overview");
  }, [
    assignedCourseRoute,
    assignedCourseWeekRoute,
    coursePathAnchor,
    pathname,
  ]);

  const navigateCourseSection = (path: string, id: string) => {
    setActiveCourseSection(id);
    const targetId = path.split("#")[1];
    const target = targetId ? document.getElementById(targetId) : null;
    go(path);
    const scroll = (element: HTMLElement) =>
      element.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      });
    if (target) scroll(target);
    else if (targetId)
      window.requestAnimationFrame(() => {
        const mountedTarget = document.getElementById(targetId);
        if (mountedTarget) scroll(mountedTarget);
      });
  };
  // The sidebar is the primary desktop nav everywhere. The top menu exists
  // only inside a course workspace, where the sidebar holds course context
  // instead — the one place the two don't duplicate each other.
  const sidebarSections = courseWorkspace
    ? courseContextSections
    : primarySections;
  const sidebarActiveId = courseWorkspace
    ? assignedCourseRoute
      ? activeCourseSection
      : ""
    : (active?.id ?? "");

  const userInitial = (user?.email?.[0] ?? "A").toUpperCase();
  // Instructors (small allowlist) get a prominent jump-to-instructor-panel
  // button in the top bar — there's otherwise no UI entry point to /instructor.
  const instructor = isInstructor(user?.email);
  // Shell chrome from the vertical's `dashboard.shellChrome`. Since the sidebar
  // became THE desktop nav surface, `hideLeftMenu` no longer removes it — it only
  // marks surfaces whose content wants the viewport (edge-to-edge, internal
  // scroll). The right Emery mentor panel keeps its own list (also gated by the
  // vertical's `supervisor` feature).
  const chrome = useMemo(
    () => resolveShellChrome(dashboard.shellChrome),
    [dashboard.shellChrome],
  );
  // The assigned FAU course keeps its human-instructor support surface even
  // though the generic Train course chrome normally hides the right rail.
  const hasFauCourseSupport =
    courseWorkspace && isFauCourse && assignedCourseRoute;
  // The crisis home banner is intentionally full-bleed. Its dashboard layout
  // supplies gutters for the content below, so the shell must not add a second
  // mobile inset around the banner itself.
  const hasFullBleedHomeBanner =
    matchesPath("/dashboard", pathname) &&
    verticalConfig.id === "crisis-negotiation";
  // Truly-immersive surfaces (the live voice/video session) are the ONLY ones
  // without the sidebar and phone tab bar — they carry their own controls.

  const desktopAppOpen =
    generalConsumer && !consumerDesktopHome && !!desktopApp;
  const showDesktopFrame = desktopAppOpen;
  const hasRightSidebar =
    !consumerDesktopHome &&
    !desktopAppOpen &&
    dashboard.features.supervisor &&
    (showRightSidebar(pathname, chrome) || hasFauCourseSupport);
  const bleed = !showLeftMenu(pathname, chrome) && !hasRightSidebar;
  const hasSidebar =
    !consumerDesktopHome && !desktopAppOpen && sidebarSections.length > 0;

  // Returning with Browser Back is the same as closing an app: once the home
  // desktop is visible, a later unrelated route must not inherit a stale frame.
  useEffect(() => {
    if (consumerDesktopHome) clearDesktopApp();
  }, [consumerDesktopHome]);

  // Hold the chrome until we know whose it is. Rendering it early publishes
  // SIGNED-OUT defaults to a signed-in user — the generic "Dashboard / Library
  // / Simulate / Debrief" rail and a "Train · Personal" header — because
  // `defaultCourseId` is still undefined. That state is fully clickable, which
  // is how cohort students ended up in places their seat cannot open. All the
  // hooks above have already run, so this only delays the first paint.
  //
  // Only for a signed-in user: public pages have no account to wait for.
  // Revalidation (tab refocus) never re-enters this state — both contexts keep
  // serving their resolved values — so the shell cannot blink after load.
  if (user && (authLoading || verticalLoading)) {
    return (
      <div className="flex min-h-dvh w-full items-center justify-center bg-[var(--paper-canvas)]">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--teal-700)] border-t-transparent"
          role="status"
          aria-label="Loading your workspace"
        />
      </div>
    );
  }

  if (verticalConfig.id === "general" && !courseWorkspace) {
    return (
      <WorkspaceShell
        key={user?.id ?? "guest"}
        pathname={navigationPath}
        onSignOut={onSignOut}
        courseHome={
          assignedCoursePath
            ? { path: assignedCoursePath, label: courseCopy.backToCourse }
            : undefined
        }
      >
        {children}
      </WorkspaceShell>
    );
  }

  return (
    // Inside an English-only course the whole shell speaks the course's
    // language, not just the page: the instructor widget and the mentor rail
    // sit out here, and a Ukrainian rail beside an English syllabus is exactly
    // the half-translation this avoids. Everywhere else this passes the
    // account locale through untouched.
    <LocaleOverride locale={shellLocale}>
      <div
        className="app-shell relative flex min-h-dvh w-full bg-[var(--paper-canvas)] font-sans text-[var(--fg)] antialiased"
        style={
          consumerDesktopHome
            ? {
                backgroundColor: "#edf5f3",
                backgroundImage:
                  "radial-gradient(circle at 16% 18%, rgba(99, 176, 169, 0.20), transparent 30%), radial-gradient(circle at 84% 12%, rgba(104, 141, 183, 0.16), transparent 31%), radial-gradient(circle at 70% 86%, rgba(221, 176, 102, 0.15), transparent 28%), linear-gradient(145deg, #f5faf8 0%, #edf4f3 48%, #f7f4ee 100%)",
              }
            : undefined
        }
      >
        <a href="#main-content" className="eq-skip-link">
          {SKIP_TO_CONTENT[shellLocale] ?? SKIP_TO_CONTENT.en}
        </a>
        <div className="app-shell-canvas fixed inset-0 -z-10" aria-hidden />

        {/* The Claude-style desktop rail. Assigned university courses use it for
          course context while stable workspace destinations live in the top
          menu; other products keep their primary navigation here. */}
        {hasSidebar && (
          <Sidebar
            sections={sidebarSections}
            activeId={sidebarActiveId}
            pathname={navigationPath}
            locale={locale}
            isInstructor={instructor}
            verticalId={verticalConfig.id}
            userInitial={userInitial}
            userEmail={user?.email ?? undefined}
            hideAccountStatus={courseWorkspace}
            showWorkspaceSwitcher={
              !isFauCourse && !!(branding.companyName || defaultCourseId)
            }
            courseContext={
              courseWorkspace
                ? {
                    organization: courseOrganization,
                    title: courseTitle,
                    term: courseTerm,
                    path: assignedCoursePath,
                  }
                : undefined
            }
            onSectionNavigate={
              courseWorkspace ? navigateCourseSection : undefined
            }
            onSignOut={onSignOut ?? (() => void signOut())}
          />
        )}

        <div
          className={`flex-1 min-w-0 flex flex-col ${
            // Focused surfaces (the live session) cap to the viewport so their inner
            // panels scroll internally; document-style pages grow and scroll the body.
            bleed ? "h-dvh overflow-hidden" : "min-h-dvh"
          }`}
        >
          {/* Top bar: phones only — desktop nav lives entirely in the sidebar, and
            full-screen surfaces (live session, supervisor) carry their own
            in-page header/controls. */}
          {!desktopAppOpen && (
            <div
              className={`mx-auto w-full shrink-0 ${consumerDesktopHome ? "" : "lg:hidden"}`}
            >
              <TopBar
                locale={locale}
                userInitial={userInitial}
                isInstructor={instructor}
                verticalId={verticalConfig.id}
                showWorkspaceSwitcher={
                  !isFauCourse && !!(branding.companyName || defaultCourseId)
                }
              />
            </div>
          )}

          {showDesktopFrame && desktopApp && (
            <header className="sticky top-0 z-50 flex min-h-14 shrink-0 items-center justify-between gap-2 border-b border-[var(--line-soft)] bg-white/90 px-2 backdrop-blur-xl sm:px-4">
              <div className="flex min-w-0 items-center gap-1 sm:gap-2">
                <button
                  type="button"
                  onClick={closeDesktopApp}
                  className="inline-flex min-h-11 shrink-0 cursor-pointer items-center gap-2 rounded-[9px] px-2.5 text-[13px] font-semibold text-[var(--ink-secondary)] transition-colors hover:bg-[var(--paper-sunken)] hover:text-[var(--ink-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] sm:px-3"
                >
                  <LayoutGrid size={17} aria-hidden="true" />
                  <span>
                    {(DESKTOP_COPY[shellLocale] ?? DESKTOP_COPY.en).desktop}
                  </span>
                </button>
                <span aria-hidden="true" className="text-[var(--ink-muted)]">
                  /
                </span>
                <div className="flex min-w-0 items-center gap-2 px-1 text-[13px] font-semibold text-[var(--ink-primary)]">
                  <span
                    aria-hidden="true"
                    className="shrink-0 text-[var(--accent)]"
                  >
                    <Icon name={desktopApp.icon} size={16} />
                  </span>
                  <span className="truncate">{desktopApp.label}</span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <div className="hidden sm:block">
                  <LanguageSelect />
                </div>
                <button
                  type="button"
                  onClick={closeDesktopApp}
                  aria-label={
                    (DESKTOP_COPY[shellLocale] ?? DESKTOP_COPY.en).close
                  }
                  title={(DESKTOP_COPY[shellLocale] ?? DESKTOP_COPY.en).close}
                  className="grid h-11 w-11 cursor-pointer place-items-center rounded-[9px] text-[var(--ink-tertiary)] transition-colors hover:bg-[var(--paper-sunken)] hover:text-[var(--ink-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                >
                  <X size={18} aria-hidden="true" />
                </button>
              </div>
            </header>
          )}

          {/* Slim content header (desktop): a quiet product ⁄ section crumb that
            gives every page the same top edge without competing with its title. */}
          {hasSidebar && !consumerDesktopHome && (
            <div
              className={
                courseWorkspace && isFauCourse
                  ? "fau-course-header sticky top-0 z-50 hidden shrink-0 lg:flex"
                  : "sticky top-0 z-50 hidden h-16 shrink-0 items-center justify-between border-b border-[var(--line-soft)] bg-[var(--paper-canvas)]/90 px-7 backdrop-blur-xl lg:flex"
              }
            >
              {courseWorkspace ? (
                <>
                  <div
                    className={`flex min-w-0 items-center gap-3 ${isFauCourse ? "fau-course-brand" : ""}`}
                  >
                    {!isFauCourse && (
                      <>
                        <ProductSwitcher />
                        <span
                          aria-hidden="true"
                          className="h-5 w-px bg-[var(--line-soft)]"
                        />
                      </>
                    )}
                    {isFauCourse ? (
                      <button
                        type="button"
                        onClick={() =>
                          navigateCourseSection(
                            `${assignedCoursePath}#course-overview`,
                            "course-overview",
                          )
                        }
                        className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-2 py-1 text-left transition-colors hover:bg-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                      >
                        {branding.logoUrl && (
                          <img
                            src={branding.logoUrl}
                            alt={`${courseOrganization} logo`}
                            className="h-8 w-8 shrink-0 border border-[var(--hairline)] bg-white object-contain p-0.5"
                          />
                        )}
                        <span className="min-w-0">
                          <span className="block ed-mono text-[8.5px] uppercase tracking-[0.14em] text-[var(--ink-muted)]">
                            {courseOrganization}
                          </span>
                          <span className="mt-0.5 block ed-display text-[13px] text-[var(--ink-primary)]">
                            {courseTitle}
                          </span>
                        </span>
                      </button>
                    ) : (
                      <span className="ed-mono text-[9px] uppercase tracking-[0.16em] text-[var(--ink-muted)]">
                        {courseOrganization}
                      </span>
                    )}
                  </div>
                  <nav
                    aria-label={`${courseOrganization} workspace`}
                    className={
                      isFauCourse
                        ? "fau-course-tabs"
                        : "flex h-full items-stretch gap-1"
                    }
                  >
                    {primarySections.map((item) => {
                      const current = item.id === active?.id;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() =>
                            item.id === "course-home"
                              ? navigateCourseSection(
                                  `${assignedCoursePath}#course-overview`,
                                  "course-overview",
                                )
                              : go(item.path)
                          }
                          aria-current={current ? "page" : undefined}
                          className={
                            isFauCourse
                              ? "fau-course-tab group"
                              : `group relative inline-flex min-h-10 cursor-pointer items-center gap-2 px-3.5 text-[13px] font-semibold tracking-[-0.01em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] ${
                                  current
                                    ? "text-[var(--ink-primary)] after:absolute after:inset-x-3.5 after:bottom-0 after:h-[2px] after:bg-[var(--accent)]"
                                    : "text-[var(--ink-secondary)] hover:text-[var(--ink-primary)]"
                                }`
                          }
                        >
                          {item.icon && (
                            <span
                              aria-hidden="true"
                              className={
                                current
                                  ? "text-[var(--accent)]"
                                  : "text-[var(--ink-tertiary)] transition-colors group-hover:text-[var(--ink-secondary)]"
                              }
                            >
                              <Icon name={item.icon} size={15} />
                            </span>
                          )}
                          {item.label}
                        </button>
                      );
                    })}
                  </nav>
                </>
              ) : (
                <div className="min-w-0">
                  <p className="ed-mono text-[9px] uppercase tracking-[0.18em] text-[var(--ink-muted)]">
                    {verticalShortLabel(verticalConfig.id, locale)} workspace
                  </p>
                  <p className="mt-0.5 truncate text-[13px] font-semibold tracking-[-0.01em] text-[var(--ink-primary)]">
                    {active
                      ? navLabel(active.id, active.label, locale)
                      : "Overview"}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="mx-auto w-full shrink-0">
            {/* Expired trial/subscription: persistent notice with the subscribe CTA.
              Trial users already see the "Trial · Nd" badge in the chrome, so the
              banner shows only once access has actually lapsed. */}
            {!consumerDesktopHome &&
              !desktopAppOpen &&
              !subscriptionLoading &&
              !hasAccess && <SubscriptionBanner />}
            {guestDemo && (
              <div
                className={
                  hasFullBleedHomeBanner ? "px-0" : "px-4 sm:px-6 lg:px-0"
                }
              >
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-[4px] border border-[var(--hairline)] border-l-2 border-l-[var(--accent)] bg-[var(--paper)] px-3 py-2">
                  <p className="text-xs font-semibold text-[var(--ink)]">
                    Demo workspace{" "}
                    <span className="font-medium text-[var(--muted)]">
                      · sample data, changes stay on this device
                    </span>
                  </p>
                  <button
                    type="button"
                    onClick={onSignOut ?? (() => void signOut())}
                    className="inline-flex min-h-9 shrink-0 cursor-pointer items-center gap-1.5 rounded-[4px] border border-[var(--hairline)] bg-[var(--paper-2)] px-3 text-xs font-semibold text-[var(--ink)] transition-colors hover:bg-[rgba(12,26,51,0.04)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
                  >
                    <LogOut size={14} aria-hidden="true" /> Exit demo
                  </button>
                </div>
              </div>
            )}
          </div>

          {bleed ? (
            /* Focused full-screen surface (the live session): top menu stays, but no
             sidebar / mentor / margins — the page fills the rest of the viewport. */
            /* key on the vertical so switching products replays the fade/slide. */
            <div
              id="main-content"
              key={verticalConfig.id}
              tabIndex={-1}
              className="flex-1 min-h-0 w-full flex flex-col animate-vertical-switch"
            >
              {children}
            </div>
          ) : (
            <>
              {/* flex-1 (not a 100dvh calc) so the row — and the mentor column
                stretching with it — always reaches the exact viewport bottom on
                short pages and grows with content on long ones. */}
              <div
                className={`mx-auto w-full pb-0 flex-1 flex flex-col ${
                  hasFullBleedHomeBanner ||
                  consumerDesktopHome ||
                  desktopAppOpen
                    ? "px-0"
                    : "px-4 sm:px-6 lg:px-0"
                }`}
              >
                <div className="flex gap-0 md:flex-1">
                  {/* flex column + min-h-0 so app-surface layouts (dashboard chat /
                    scenario) get a height context and scroll internally, while
                    document-style pages grow and scroll the body. */}
                  <div
                    id="main-content"
                    key={verticalConfig.id}
                    tabIndex={-1}
                    className="flex-1 min-w-0 flex flex-col min-h-0 animate-vertical-switch"
                  >
                    {children}
                  </div>
                  {/* Right rail (2xl+): the Message-instructor widget sits ABOVE the AI
                    mentor card; the gray frame + sticky inset are owned here. */}
                  {hasRightSidebar && (
                    <aside
                      aria-label="Learning support"
                      className="shrink-0 w-[368px] hidden 2xl:block"
                    >
                      <div className="sticky top-6 pl-3 pr-7 pt-3 pb-6 space-y-3">
                        <MessageInstructorWidget
                          variant="rail"
                          contact={
                            hasFauCourseSupport
                              ? FAU_INSTRUCTOR_CONTACT
                              : undefined
                          }
                        />
                        <MentorPanel />
                      </div>
                    </aside>
                  )}
                </div>
              </div>

              {/* Below 2xl the right rail is hidden, so the Message-instructor entry
                point becomes a floating pill above the bottom nav. */}
              {hasRightSidebar && (
                <MessageInstructorWidget
                  variant="fab"
                  contact={
                    hasFauCourseSupport ? FAU_INSTRUCTOR_CONTACT : undefined
                  }
                />
              )}
            </>
          )}

          {/* Phone bottom nav — on every surface (bleed or not) except the immersive
            live session. Lives inside the content column so its spacer keeps page
            content clear of the fixed bar. */}
          {hasSidebar && (
            <MobileNav
              sections={primarySections}
              activeId={active?.id ?? ""}
              locale={locale}
            />
          )}
        </div>

        {/* Personal quick-access shortcut to the Course Studio — shown ONLY for the
          general owner test account (STUDIO_QUICK_ACCESS_EMAIL), on every in-app
          page, regardless of vertical/chrome. */}
        {user?.email === STUDIO_QUICK_ACCESS_EMAIL && (
          <button
            type="button"
            onClick={() => (window as any).navigate?.("/studio")}
            aria-label="Open Course Studio"
            className="fixed z-40 bottom-5 right-5 inline-flex items-center gap-1.5 h-11 px-4 rounded-full bg-[var(--action-bg)] text-white ed-mono text-[11px] tracking-wide shadow-lg hover:bg-[var(--action-bg-hover)] active:scale-[0.98] transition cursor-pointer"
            style={{ marginBottom: "env(safe-area-inset-bottom)" }}
          >
            <Sparkles size={15} className="shrink-0" />
            <span>Studio</span>
          </button>
        )}
      </div>
    </LocaleOverride>
  );
}
